// src/components/Replan/MeasurementSummaryShape.js
import React, { useMemo, useState } from 'react';
import { Group as KonvaGroup, Rect, Text, Circle } from 'react-konva';
import DrawingText from './DrawingText';
import ResizeAnchor from './ResizeAnchor';
import { useTranslation } from 'react-i18next';
import { getShapeMeasurementData } from '../utils/measurementUtils';
import { TOOLS } from '../constants/drawingTools';
import { resolveAllParams, math } from '../../ShapeList/paramUtils';
import { getDisplayMeasurement, getDisplayAreaMeasurement, resolveShapeScaleProps } from '../utils/drawingUtils';
import { hexToRgba } from '../utils/colorUtils';

/**
 * MeasurementSummaryShape - A draggable summary box shape showing aggregated measurements
 * grouped by a specified key, tracking specific shapes.
 * This is a SHAPE (like Rectangle, Polygon) that gets saved to Firebase.
 */
const MeasurementSummaryShape = ({
  shape,
  commonProps,
  isSelected,
  stageScale = 1,
  shapeStroke,
  onUpdateShape,
  allShapes = [], // All shapes in the drawing
  currentScaleProps,
  dpi,
  imageSize,
  onShapeSelect, // Function to update selection
  allScales = [], // All scales for per-shape scale resolution
}) => {
  const { t } = useTranslation();
  const [isDraggingCorner, setIsDraggingCorner] = useState(false);

  // Extract shape properties
  const {
    trackedShapeIds = [],
    groupingKey = '',
    width = 280,
  } = shape;

  // Filter shapes to only tracked ones
  const trackedShapes = useMemo(() => {
    const idSet = new Set(trackedShapeIds);
    return allShapes.filter(s => idSet.has(s.id));
  }, [allShapes, trackedShapeIds]);

  // Calculate aggregated totals grouped by grouping key
  const groupedTotals = useMemo(() => {
    if (!trackedShapes.length) return [];

    const groups = new Map();

    trackedShapes.forEach(shape => {
      // Determine group name
      let groupName = t('replan.measurementSummary.all_items');
      if (groupingKey) {
        if (groupingKey === '__page__') {
          const pageIdx = shape?.pageInfo?.pageIndex ?? shape?.pageInfo?.index;
          groupName = pageIdx !== undefined && pageIdx !== null
            ? `${t('replan.measurementSummary.page')} ${pageIdx + 1}`
            : shape?.pageInfo?.name || t('replan.measurementSummary.unknown_page');
        } else {
          groupName = shape?.params?.[groupingKey] || t('replan.measurementSummary.not_set');
        }
      }

      // Get measurement data for this shape using its own scale if locked
      const shapeScaleProps = resolveShapeScaleProps(shape, currentScaleProps, allScales);
      const measureData = getShapeMeasurementData(shape, shapeScaleProps, dpi, imageSize);

      // Initialize group if needed
      if (!groups.has(groupName)) {
        groups.set(groupName, {
          name: groupName,
          totalLengthRaw: 0, // in pixels
          totalAreaRaw: 0, // in square pixels
          count: 0,
          shapeTypes: new Set(),
          shapes: [], // Store all shapes for param resolution
          colors: new Set(), // Track unique colors
          baseUnit: '',
        });
      }

      const group = groups.get(groupName);
      group.count++;
      group.shapeTypes.add(shape.type);
      group.shapes.push(shape);
      if (shape.stroke || shape.color) {
        group.colors.add(shape.stroke || shape.color);
      }

      // Aggregate raw measurement values, converting to page-scale equivalents
      const pageRatio = currentScaleProps?.ratio;
      const shapeRatio = shapeScaleProps?.ratio;

      if ([TOOLS.MEASURE, TOOLS.LINE, TOOLS.POLYLINE, TOOLS.COTATION, TOOLS.ARROW].includes(shape.type)) {
        if (measureData.rawValue > 0) {
          const linearFactor = (pageRatio && shapeRatio) ? shapeRatio / pageRatio : 1;
          group.totalLengthRaw += measureData.rawValue * linearFactor;
          if (!group.baseUnit) group.baseUnit = measureData.rawUnit;
        }
      } else if (shape.type === TOOLS.AREA) {
        const areaValue = shape.netArea !== undefined ? shape.netArea : (measureData.rawValue || 0);
        if (areaValue > 0) {
          const linearFactor = (pageRatio && shapeRatio) ? shapeRatio / pageRatio : 1;
          group.totalAreaRaw += areaValue * linearFactor * linearFactor;
          if (!group.baseUnit) group.baseUnit = measureData.rawUnit;
        }
      }
    });

    // Now for each group, calculate the aggregated measurement and resolve params
    const result = Array.from(groups.values()).map(group => {
      // Calculate total measurement display strings (like ShapeList does)
      // IMPORTANT: Use English keys (not translated) so formulas can reference them
      const calculatedParams = {};

      if (group.totalLengthRaw > 0) {
        const totalDisplay = getDisplayMeasurement(
          group.totalLengthRaw,
          currentScaleProps,
          dpi,
          imageSize,
        );
        calculatedParams['Total Length'] = `${totalDisplay.value} ${totalDisplay.unit}`;
      }

      if (group.totalAreaRaw > 0) {
        const totalDisplay = getDisplayAreaMeasurement(
          group.totalAreaRaw,
          currentScaleProps,
          dpi,
        );
        calculatedParams['Total Area'] = `${totalDisplay.value} ${totalDisplay.unit}`;
      }

      // Use representative shape (first shape) for param template
      const representativeShape = group.shapes[0];

      // Add Diameter if needed (from representative shape)
      if (representativeShape.type === TOOLS.POINT) {
        const radius = representativeShape.radius || 0;
        calculatedParams['Diameter'] = (radius * 2).toFixed(2);
      }

      // Resolve all params using the aggregated measurements (EXACT ShapeList logic)
      const allResolvedParams = resolveAllParams(
        representativeShape.params,
        calculatedParams
      );

      return {
        name: group.name,
        count: group.count,
        calculatedParams,
        resolvedParams: allResolvedParams,
        totalLengthDisplay: calculatedParams['Total Length'],
        totalAreaDisplay: calculatedParams['Total Area'],
        colors: Array.from(group.colors), // Convert Set to Array
      };
    });

    return result;
  }, [trackedShapes, groupingKey, currentScaleProps, dpi, imageSize, allScales]);

  // All sizes proportional to fontSize — one master clamp, everything else scales
  const scale = stageScale > 0 ? stageScale : 1;
  const fontSize = Math.max(14, Math.min(28, 12 / scale));
  const titleFontSize = fontSize * 1.17;
  const lineHeight = fontSize * 1.05;
  const padding = fontSize;
  const headerHeight = fontSize * 2.5;
  const groupSpacing = fontSize * 0.67;
  const indent = fontSize * 0.83;
  const colorRadius = fontSize * 0.33;
  const colorSpacing = fontSize * 0.83;
  const selectBtnSize = fontSize * 1.33;

  // Calculate content lines
  let totalLines = 0;
  groupedTotals.forEach(group => {
    totalLines++; // Group name
    if (group.totalLengthDisplay) totalLines++;
    if (group.totalAreaDisplay) totalLines++;

    // Count param lines (exclude calculated keys and internal keys)
    const paramKeys = Object.keys(group.resolvedParams).filter(k =>
      k !== 'order' && !['Total Length', 'Total Area', 'Diameter'].includes(k)
    );
    totalLines += paramKeys.length;

    totalLines++; // Count line
    totalLines++; // Spacer
  });

  const contentHeight = totalLines * lineHeight + padding / 2; // Add padding at bottom
  const boxWidth = Math.max(180, Math.min(700, fontSize * 23));
  const boxHeight = headerHeight + contentHeight;

  // Compute cursor based on draggability, consistent across anchors
  const anchorLeaveCursor = commonProps.draggable ? 'move' : 'pointer';

  // Handle selecting tracked shapes
  const handleSelectTracked = () => {
    if (onShapeSelect && trackedShapeIds.length > 0) {
      onShapeSelect(trackedShapeIds, true); // Select all tracked shapes
    }
  };

  // Handle top-left corner drag (moves entire shape)
  const handleTopLeftDrag = (e) => {
    if (!onUpdateShape) return;
    const node = e.target;
    const newX = shape.x + node.x();
    const newY = shape.y + node.y();

    onUpdateShape(shape.id, {
      x: newX,
      y: newY
    });

    // Reset anchor position
    node.position({ x: 0, y: 0 });
  };

  // Handle bottom-right corner drag (resizes box)
  const handleBottomRightDrag = (e) => {
    if (!onUpdateShape) return;
    const node = e.target;
    const newWidth = Math.max(200, node.x() * (stageScale > 0 ? stageScale : 1));

    onUpdateShape(shape.id, {
      width: newWidth
    });
  };

  // Render content lines
  const renderContent = () => {
    const lines = [];
    let yOffset = headerHeight + padding;

    groupedTotals.forEach((group, groupIndex) => {
      // Group name header
      lines.push(
        <DrawingText
          key={`group-${groupIndex}-name`}
          x={padding*2}
          y={yOffset}
          text={group.name}
          fontSize={fontSize}
          fontStyle="bold"
          fill={commonProps.stroke}
          listening={false}
          useBadge={false}
          stageScale={stageScale}
          align="left"
        />
      );
      const groupStartY = yOffset;

      yOffset += lineHeight;

      // Total length (from aggregated measurement)
      if (group.totalLengthDisplay) {
        lines.push(
          <DrawingText
            key={`group-${groupIndex}-length`}
            x={padding + indent}
            y={yOffset}
            text={`${t('replan.measurementSummary.length_label')}: ${group.totalLengthDisplay}`}
            fontSize={fontSize}
            fill={commonProps.stroke}
            listening={false}
            useBadge={false}
            stageScale={stageScale}
            align="left"
          />
        );
        yOffset += lineHeight;
      }

      // Total area (from aggregated measurement)
      if (group.totalAreaDisplay) {
        lines.push(
          <DrawingText
            key={`group-${groupIndex}-area`}
            x={padding + indent}
            y={yOffset}
            text={`${t('replan.measurementSummary.area_label')}: ${group.totalAreaDisplay}`}
            fontSize={fontSize}
            fill={commonProps.stroke}
            listening={false}
            useBadge={false}
            stageScale={stageScale}
            align="left"
          />
        );
        yOffset += lineHeight;
      }

      // All other resolved params (excluding calculated ones we already showed)
      const paramKeys = Object.keys(group.resolvedParams)
        .filter(k => k !== 'order' && !['Total Length', 'Total Area', 'Diameter'].includes(k))
        .sort();

      paramKeys.forEach((paramKey) => {
        const value = group.resolvedParams[paramKey];
        let displayValue = '';

        // Format the resolved value (same as ShapeList does)
        if (value?.error) {
          displayValue = value.error;
        } else if (value instanceof math.Unit) {
          displayValue = math.format(value, { notation: 'fixed', precision: 2 });
        } else if (typeof value === 'number') {
          displayValue = value.toFixed(2);
        } else {
          displayValue = String(value);
        }

        if (displayValue) {
          lines.push(
            <DrawingText
              key={`group-${groupIndex}-param-${paramKey}`}
              x={padding + indent}
              y={yOffset}
              text={`${paramKey}: ${displayValue}`}
              fontSize={fontSize * 0.95}
              fill={commonProps.stroke}
              opacity={0.8}
              listening={false}
              useBadge={false}
              stageScale={stageScale}
              align="left"
            />
          );
          yOffset += lineHeight;
        }
      });

      // Count
      lines.push(
        <DrawingText
          key={`group-${groupIndex}-count`}
          x={padding + indent}
          y={yOffset}
          text={`${t('replan.measurementSummary.count_label')}: ${group.count}`}
          fontSize={fontSize * 0.9}
          fill={commonProps.stroke}
          opacity={0.7}
          listening={false}
          useBadge={false}
          stageScale={stageScale}
          align="left"
        />
      );
      // Vertical color bar on left edge of the group
      if (group.colors && group.colors.length > 0) {
        const barHeight = yOffset - groupStartY + groupSpacing;
        const barColor = group.colors[0];
        lines.push(
          <Rect
            key={`group-${groupIndex}-colorbar`}
            x={padding - 3}
            y={groupStartY}
            width={fontSize * 0.45}
            height={barHeight}
            fill={barColor || '#000000'}
            opacity={0.85}
            listening={false}
          />
        );
      }
      yOffset += lineHeight + groupSpacing;
    });

    return lines;
  };

  return (
    <KonvaGroup
      {...commonProps}
      draggable={commonProps.draggable && !isDraggingCorner}
    >
      {/* Background - uses shape color like RectangleShape */}
      <Rect
        width={boxWidth}
        height={boxHeight}
        stroke={hexToRgba(commonProps.stroke, 0.8)}
        fill={hexToRgba(commonProps.stroke, 0.1)}
        strokeWidth={commonProps.strokeWidth || 1}
        dash={[20, 5]}
        listening={true}
        {...(isSelected
          ? {
            shadowColor: commonProps.shadowColor,
            shadowBlur: commonProps.shadowBlur,
            shadowOpacity: commonProps.shadowOpacity,
            shadowEnabled: commonProps.shadowEnabled,
          }
          : {})}
      />

      {/* Header background - slightly more opaque */}
      <Rect
        width={boxWidth}
        height={headerHeight}
        fill={hexToRgba('#9191afff', 0.55)}
        listening={false}
      />

      {/* Title - use contrasting color */}
      <DrawingText
        x={padding}
        y={padding}
        text={t('replan.measurementSummary.title')}
        fontSize={titleFontSize}
        fontFamily="Arial Regular"
        fontStyle="bold"
        fill={commonProps.stroke}
        listening={false}
        useBadge={false}
        stageScale={stageScale}
        align="left"
      />

      {/* Select Tracked Button (only visible when selected) */}
      {isSelected && (
        <KonvaGroup
          x={boxWidth - (selectBtnSize) - padding}
          y={padding - 2}
          onClick={handleSelectTracked}
          onTap={handleSelectTracked}
        >
          <Rect
            width={selectBtnSize}
            height={selectBtnSize}
            fill="transparent"
            listening={true}
            onMouseEnter={(e) => {
              e.target.fill(hexToRgba(commonProps.stroke, 0.2));
              e.target.getLayer()?.batchDraw();
            }}
            onMouseLeave={(e) => {
              e.target.fill('transparent');
              e.target.getLayer()?.batchDraw();
            }}
          />
          <Text
            x={(selectBtnSize) / 2}
            y={(selectBtnSize) / 2}
            text="👁"
            fontSize={fontSize * 0.8}
            fill={commonProps.stroke}
            offsetX={(fontSize * 0.8) / 2}
            offsetY={(fontSize * 0.8) / 2}
            listening={false}
          />
        </KonvaGroup>
      )}

      {/* Content */}
      {renderContent()}

      {/* Control Points - Only visible when selected */}
      {isSelected && (
        <>
          <ResizeAnchor
            x={0}
            y={0}
            stageScale={stageScale}
            color={commonProps.stroke}
            cursor={anchorLeaveCursor}
            hoverCursor="move"
            onDragMove={handleTopLeftDrag}
            onDragStart={() => setIsDraggingCorner(true)}
            onDragEnd={() => setIsDraggingCorner(false)}
          />
          <ResizeAnchor
            x={boxWidth}
            y={boxHeight}
            stageScale={stageScale}
            color={commonProps.stroke}
            cursor={anchorLeaveCursor}
            hoverCursor="nwse-resize"
            onDragMove={handleBottomRightDrag}
            onDragStart={() => setIsDraggingCorner(true)}
            onDragEnd={() => setIsDraggingCorner(false)}
          />
        </>
      )}
    </KonvaGroup>
  );
};

export default MeasurementSummaryShape;
